import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export function QrLoginPanel({ onUseEmail }) {
  const { loginWithToken } = useAuth();
  const [session, setSession] = useState(null);
  const [expired, setExpired] = useState(false);
  const timer = useRef();
  const nav = useNavigate();

  const start = async () => {
    setExpired(false);
    setSession(null);
    try { const { data } = await api.post("/auth/qr/session"); setSession(data); }
    catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { start(); }, []);

  useEffect(() => {
    if (!session?.id) return;
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/auth/qr/session/${session.id}`);
        if (data.status === "APPROVED") {
          clearInterval(timer.current);
          loginWithToken(data.token, data.user);
          toast.success("Prijava uspješna");
          nav(data.user?.role === "ADMIN" ? "/admin/dashboard" : "/dashboard");
        } else if (data.status === "EXPIRED" || data.status === "REJECTED") {
          clearInterval(timer.current);
          setExpired(true);
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(timer.current);
  }, [session?.id]);

  const steps = [
    "Skenirajte QR kod kamerom mobitela",
    "Dodirnite vezu kako biste otvorili StreamPay",
    "Za povezivanje sa svojim korisničkim računom potvrdite prijavu na mobitelu",
  ];

  return (
    <div className="card-dark p-6 sm:p-10" data-testid="qr-login-panel">
      <h1 className="font-heading font-black text-2xl sm:text-3xl text-[#FFD60A] mb-8">Prijava na StreamPay</h1>
      <div className="grid md:grid-cols-[1fr_auto] gap-8 md:gap-10 items-start">
        <div>
          <ol className="space-y-5">
            {steps.map((s, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="w-9 h-9 shrink-0 rounded-full border border-white/40 text-white flex items-center justify-center font-heading font-bold">{i + 1}</span>
                <span className="text-white/85 text-sm sm:text-base pt-1.5">{s}</span>
              </li>
            ))}
          </ol>
          <button onClick={() => toast.info("Otvorite StreamPay na mobitelu, prijavite se i skenirajte QR kod kamerom.")} className="text-[#FFD60A] underline text-sm mt-7" data-testid="qr-help">
            Treba li vam pomoć?
          </button>
        </div>

        <div className="flex flex-col items-center gap-4">
          <div className="bg-white p-3 rounded-xl w-[220px] h-[220px] flex items-center justify-center relative">
            {session && !expired ? (
              <img src={`${API}/api/auth/qr/session/${session.id}/qr.png`} alt="QR kod za prijavu" className="w-full h-full object-contain" data-testid="qr-image" />
            ) : (
              <div className="text-center">
                <p className="text-black/60 text-sm mb-3">{expired ? "QR kod je istekao" : "Učitavanje…"}</p>
                {expired && (
                  <button onClick={start} className="btn-yellow rounded-full px-4 h-9 inline-flex items-center gap-2 text-sm" data-testid="qr-refresh">
                    <RefreshCw size={14} /> Novi kod
                  </button>
                )}
              </div>
            )}
          </div>
          <button onClick={onUseEmail} className="text-[#FFD60A] text-sm hover:underline" data-testid="use-email-login">
            Prijavite se E-Mail-om &nbsp;›
          </button>
        </div>
      </div>
    </div>
  );
}
